import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import { ClipboardCheck, ShieldAlert } from "lucide-react";

export default function TruckInspectionDialog({ open, onOpenChange, onSaved, date }) {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null); // {template, required_today, inspection}
  const [items, setItems] = useState([]); // [{name, checked, note}]
  const [notes, setNotes] = useState("");
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // Pass through the target `date` (from back-finalize) so the inspection
    // GET + POST both apply to the day being finalized, not today. Without
    // this, an employee back-finalizing a past day sees "no inspection" even
    // after they just submitted one because the record was filed under
    // today's date on the server. This caused the "must complete inspection"
    // loop bug (Aug 2026 hotfix).
    api.get("/truck-inspections/today", { params: date ? { date } : {} }).then(({ data }) => {
      setState(data);
      const existing = data.inspection;
      if (existing) {
        // merge template w/ existing to preserve order from template
        const map = Object.fromEntries((existing.items || []).map((i) => [i.name, i]));
        setItems(
          (data.template || []).map((name) => ({
            name,
            checked: !!map[name]?.checked,
            note: map[name]?.note || "",
          }))
        );
        setNotes(existing.notes || "");
      } else {
        setItems((data.template || []).map((name) => ({ name, checked: false, note: "" })));
        setNotes("");
      }
      setSignature("");
      setLoading(false);
    }).catch((e) => {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLoading(false);
    });
  }, [open, date]);

  const toggle = (idx) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it));
  const setNote = (idx, v) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, note: v } : it));

  const allChecked = items.length > 0 && items.every((i) => i.checked);
  const uncheckedCount = items.filter((i) => !i.checked).length;
  const uncheckedNoNote = items.filter((i) => !i.checked && !i.note.trim()).length;

  const submit = async () => {
    if (items.length === 0) { toast.error("No checklist items configured. Ask admin to add some."); return; }
    if (!signature) { toast.error("Signature required"); return; }
    if (uncheckedCount > 0 && uncheckedNoNote > 0) {
      toast.error(`Please add a note for each unchecked item (${uncheckedNoNote} missing).`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/truck-inspections", {
        items: items.map((i) => ({ name: i.name, checked: i.checked, note: i.note })),
        notes,
        signature_data: signature,
        // Same reasoning as the GET above — file this inspection against the
        // day the caller is actually working on, so the finalize endpoint
        // can find it.
        ...(date ? { date } : {}),
      });
      toast.success(allChecked ? "Inspection completed" : "Inspection submitted with notes");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="truck-inspection-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-sky-700" />
            Truck Pre-Inspection
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-500 text-sm">Loading…</div>
        ) : (
          <div className="space-y-3">
            {state?.required_today && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900 flex items-start gap-2" data-testid="inspection-required-banner">
                <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>This inspection is required today before you can finalize your day.</span>
              </div>
            )}
            {state?.inspection && (
              <div className="rounded-lg bg-sky-50 border border-sky-200 p-2 text-xs text-sky-900">
                Already submitted today — re-submit to update.
              </div>
            )}

            {items.length === 0 && (
              <p className="text-sm text-slate-500">No inspection items configured. Ask your admin to set up the checklist in Settings.</p>
            )}

            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50/40 p-3" data-testid={`inspection-item-${idx}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={it.checked}
                      onCheckedChange={() => toggle(idx)}
                      className="mt-0.5"
                      data-testid={`inspection-check-${idx}`}
                    />
                    <span className={`text-sm flex-1 ${it.checked ? "text-slate-900 font-semibold" : "text-slate-700"}`}>
                      {it.name}
                    </span>
                  </label>
                  {!it.checked && (
                    <Input
                      value={it.note}
                      onChange={(e) => setNote(idx, e.target.value)}
                      placeholder="If unchecked, explain what happened…"
                      className="mt-2 text-sm"
                      data-testid={`inspection-note-${idx}`}
                    />
                  )}
                </div>
              ))}
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">General Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="mt-1 text-sm"
                placeholder="Anything else worth flagging?"
                data-testid="inspection-notes-input"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Lead Technician Signature</Label>
              <div className="mt-1">
                <SignaturePad value={signature} onChange={setSignature} testId="inspection-signature-pad" />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Cancel</Button>
              <Button
                onClick={submit}
                disabled={busy || items.length === 0}
                className="flex-1 bg-sky-600 hover:bg-sky-700"
                data-testid="inspection-submit-btn"
              >
                {busy ? "Saving…" : (allChecked ? "Submit ✓" : "Submit with notes")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
